import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { LocalStorageService } from '../../../services/local-storage.service';
import { AccountService } from '../../../services/account.service';
import { AccountingSystemService } from '../../../integrations/accounting_systems/shared/services/accounting_system.service';
import { AcsConfig, AcsSelectedRecord } from '../../../module/acs-support/acs-support';
import { BehaviorSubject } from 'rxjs';
import { LooseObject } from '../../../objects/loose-object';
import { ActivitiesService } from '../../../services/activities.service';
import moment from 'moment';
import { NotificationService } from '../../../services/notification.service';
import { AcsSupportService } from '../../../module/acs-support/acs-support.service';
import { data_get, fallback, filled, spf } from '../../utils/common';
import { ClientStoreService } from '../../../services/client-store.service';
import { TranslateService } from '@ngx-translate/core';


@Injectable()
export class LayoutDashboardService {
  public showedAside = false;
  public minimizedAside = false;
  public showHeaderSearchForm = false;
  public bSupportWindowMinimize = true;
  private _objAcsConfig: AcsConfig;

  public bCallInit = false;
  public bAfterCall = false;
  public objSelectedRecord: AcsSelectedRecord = null;

  private currentRecord = new BehaviorSubject<any[]>(this.getRecentRecords());

  record$ = this.currentRecord.asObservable();

  /**
   * Save in this service the currently viewed record
   * so our dashboards would know to preselect.
   *
   * @var {LooseObject}
   */
  public objCurrentViewedRecord: LooseObject = null;

  /**
   * Holder for which is parent is active.
   *
   * @var {string}
   */
  public strActiveParent: string = null;

  acsStatus: BehaviorSubject<boolean>;

  constructor(
    private localStorageService: LocalStorageService,
    private router: Router,
    private accountService: AccountService,
    private accounting: AccountingSystemService,
    private activitiesService: ActivitiesService,
    private notifService: NotificationService,
    private acsSupportService: AcsSupportService,
    private clientStoreService: ClientStoreService,
    private translateService: TranslateService
    ) {
    if (this.localStorageService.getItem("navbar") !== null) {
      this.minimizedAside = JSON.parse(this.localStorageService.getItem("navbar"));
    }

    this.acsStatus = new BehaviorSubject<boolean>(false);
  }

  hideAside() {
    this.showedAside = false;
  }

  showAside() {
    this.showedAside = true;
  }

  toggleAside() {
    this.showedAside = !this.showedAside;
  }

  toggleSupportWindowMinimize() {
    this.bSupportWindowMinimize = !this.bSupportWindowMinimize;
  }

  minimizeAside() {
    this.minimizedAside = !this.minimizedAside;
    this.localStorageService.setItem("navbar", this.minimizedAside.toString());
  }

  showedHeaderSearchForm() {
    this.showHeaderSearchForm = !this.showHeaderSearchForm;
  }

  /**
   * Redirect the user back to the client selection page.
   *
   * @returns {void}
   */
  switchClient(): void {
    this.localStorageService.removeItem('xero_short_code');
    this.localStorageService.removeItem('last_dashboard_accessed');
    this.router.navigateByUrl('/account/selection');
    this.accountService.triggerSwitchAccount();
    this.accounting.clearStoredAccountingSystemIdentity();
  }

  /**
   * If searchable text has a multiple values.
   * extract the values that contains of search
   * term.
   * @param data
   * @param matchPercentage
   * @param term
   */
  extractSearchValues(data, matchPercentage, term): Array<Object> {
    let listOfHits = [];

    data['name'].forEach(searchValue => {
      // Get only if the searchable text contains the term
      if (searchValue.toLowerCase().includes(term.toLowerCase())) {
        let newHits = {
          id: data['id'],
          name: searchValue,
          client_id: data['client_id'],
          module: data['module'],
          match_percentage: matchPercentage
        };

        listOfHits.push(newHits);
      }
    });

    return listOfHits;
  }

  public get acsConfig(): AcsConfig {
    return this._objAcsConfig
  }

  public set acsConfig(objAcsConfig: AcsConfig) {
    this._objAcsConfig = objAcsConfig;
    this.acsStatus.next(objAcsConfig.enable);
  }

   /**
   * Log the call summary and save it as a phone call activity
   *
   * @return  {void}
   */
  logCallEvent(strCallNotes: string): void {
    const objModuleField = {
      contacts: 'contact_id',
      sites: 'site_id',
      customers: 'customer_id',
    };
    let objCallData = {
      "module_id": this.objSelectedRecord.id,
      "module_field": objModuleField[this.objSelectedRecord.module],
      "assigned_to": this.localStorageService.getItem('user_id'),
      "subject": "phone",
      "note": strCallNotes,
      "module": this.objSelectedRecord.module,
      "type": "event",
      "activity_date": moment.utc().format('YYYY-MM-DD HH:MM:SS')
    };
    this.activitiesService.createActivity(JSON.stringify(objCallData)).subscribe( data => {
      // This will close the call summary window.
      this.bAfterCall = false;
      this.bCallInit = false;
      this.notifService.notifySuccess('activity_created');
      this.acsSupportService.triggerPingAcs('activities');
    }, err => {
      if (err['error'] && err['error']['contact_id']) {
        this.notifService.notifyWarning('not_allowed', err['error']['contact_id'][0]);
      }
    });
  }

  private  getRecentRecords(): any[] {
    let currentClient = this.clientStoreService.getActiveClient();
    if (filled(currentClient)) {
      const cacheKey = 'recent_records:' + currentClient.client_id;
      let recentRecords = fallback(this.localStorageService.getJsonItem(cacheKey), {
        fallback: () => []
      });
      return recentRecords;
    }
  }

  updateRecentRecords(currentRecord, currentModule): void {
    let name;
    let modulesWithText = ['supplier_invoices', 'customer_invoices', 'purchase_orders', 'contacts', 'leads'];
    let modulesWithCustomerText = ['recurring_jobs', 'recurring_invoices', 'credit_notes'];
    let currentClient = this.clientStoreService.getActiveClient();
    const cacheKey = 'recent_records:' + currentClient.client_id;
    let recentRecords = fallback(this.getRecentRecords(), {
      fallback: () => []
    });

    if (filled(currentModule)) {
      if (currentModule == 'jobs') {
        name = data_get(currentRecord, 'job_number');
      } else if (currentModule == 'opportunities') {
        name = data_get(currentRecord, 'opportunity_number');
      } else if (currentModule == 'work_orders') {
        name = data_get(currentRecord, 'work_order_number');
      } else if (currentModule == 'stock_levels') {
        name = data_get(currentRecord, 'product_name');
      } else if (currentModule == 'activities') {
        name = data_get(currentRecord, 'activity_name');
      } else if (currentModule == 'timesheets') {
        name = data_get(currentRecord.user, 'name');
      } else if (currentModule == 'project_templates') {
        name = data_get(currentRecord, 'project_name');
      } else if (currentModule == 'exports') {
        let exportLogs = data_get(currentRecord, 'logs');
        if (exportLogs.length > 1) {
          name = exportLogs.map( objLog => {
            return this.translateService.instant(objLog.module);
          }).join(", ");
        } else {
          name = this.translateService.instant(data_get(exportLogs[0], 'module'))
        }
      } else if (currentModule == 'imports') {
        currentModule = 'import';
        name = this.translateService.instant(data_get(currentRecord, 'module'));
      } else if (currentModule == 'customers') {
        name = spf('%s | %s | %s', {
          args: [data_get(currentRecord, 'name'), data_get(currentRecord, 'customer_type'), data_get(currentRecord, 'address_text')]
        });
      } else if (currentModule == 'checklists') {
        if (data_get(currentRecord, 'type') == 'assets') {
          name = spf('%s (%s)', {
            args: [data_get(currentRecord, 'name'), data_get(currentRecord, 'asset_type_text')]
          });
        } else {
          name = data_get(currentRecord, 'text');
        }
      } else if (currentModule == 'contracts') {
        name = data_get(currentRecord, 'contract_number');
      } else if (modulesWithCustomerText.includes(currentModule)) {
        name = data_get(currentRecord, 'customer_text');
      } else if (modulesWithText.includes(currentModule)) {
        name = data_get(currentRecord, 'text');
      } else {
        name = data_get(currentRecord, 'name');
      }
    }

    const indexOfExistingRecord = recentRecords.findIndex(record =>
      record.id == currentRecord.id);

    if (Object.keys(recentRecords).length >= 20 && indexOfExistingRecord === -1) {
      recentRecords.pop();
    }

    if (indexOfExistingRecord !== -1) {
      recentRecords.splice(indexOfExistingRecord, 1);
    }

    if (currentModule === 'stock_levels') {
      recentRecords.unshift({
        id: data_get(currentRecord, 'id', null),
        name: name,
        module: currentModule,
        item_id: data_get(currentRecord, 'item_id', null)
      });
    } else if (currentModule === 'timesheets') {
      recentRecords.unshift({
        id: data_get(currentRecord, 'id', null),
        name: name,
        module: currentModule,
        user_id: data_get(currentRecord.user, 'id', null)
      });
    } else if (currentModule === 'project_templates') {
      let projectTemplate = {
        created_at: data_get(currentRecord, 'created_at'),
        id: data_get(currentRecord, 'id'),
        modified_by: data_get(currentRecord, 'modified_by'),
        project_name: data_get(currentRecord, 'project_name'),
        text: data_get(currentRecord, 'text'),
        updated_at: data_get(currentRecord, 'updated_at'),
      };
      recentRecords.unshift({
        id: data_get(currentRecord, 'id', null),
        name: name,
        module: currentModule,
        others: projectTemplate
      });
    } else {
      recentRecords.unshift({
        id: data_get(currentRecord, 'id', null),
        name: name,
        module: currentModule,
      });
    }

    this.localStorageService.setJsonItem(cacheKey, recentRecords);
    this.currentRecord.next(this.getRecentRecords());
  }

  deleteRecentRecord(recordId) {
    let currentClient = this.clientStoreService.getActiveClient();
    const cacheKey = 'recent_records:' + currentClient.client_id;
    let recentRecords = fallback(this.getRecentRecords(), {
      fallback: () => []
    });

    const indexOfExistingRecord = recentRecords.findIndex(record =>
      record.id == recordId);

    if (indexOfExistingRecord !== -1) {
      recentRecords.splice(indexOfExistingRecord, 1);
    }

    this.localStorageService.setJsonItem(cacheKey, recentRecords);
    this.currentRecord.next(this.getRecentRecords());
  }

  deleteMultipleRecentRecord(records) {
    let currentClient = this.clientStoreService.getActiveClient();
    const cacheKey = 'recent_records:' + currentClient.client_id;
    let recentRecords = fallback(this.getRecentRecords(), {
      fallback: () => []
    });

    let recordIds = data_get(records, 'record_ids');

    if (filled(recentRecords)) {
      recentRecords = recentRecords.filter(record =>
        !recordIds.some(deleteRecord => deleteRecord.id === record.id)
      );

      this.localStorageService.setJsonItem(cacheKey, recentRecords);
      this.currentRecord.next(this.getRecentRecords());
    }
  }
}
